import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import * as THREE from 'three';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';


const ContactPage = () => {
    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        });

        // Additional fallback methods
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE, and Opera
    }, []);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        service: 'general'
    });

    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formError, setFormError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Form validation
        if (!formData.name || !formData.email || !formData.message) {
            setFormError(true);
            setErrorMessage('Please fill in all required fields.');
            return;
        }

        setIsSubmitting(true);
        setFormError(false);

        try {
            // Submit to Firestore
            const docRef = await addDoc(collection(db, "contactSubmissions"), {
                ...formData,
                timestamp: serverTimestamp(),
                source: 'website-contact-form'
            });

            console.log("Form submitted with ID:", docRef.id);

            // Show success message
            setFormSubmitted(true);

            // Reset form after submission
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: '',
                service: 'general'
            });
        } catch (error) {
            console.error("Error submitting form:", error);
            setFormError(true);
            setErrorMessage('There was a problem submitting your form. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Function to handle navigation back to home
    const navigateToHome = () => {
        window.location.href = '/';
    };

    // Function to navigate to other pages
    const navigateTo = (page) => {
        window.location.href = `/${page}`;
    };

    return (
        <div className="min-h-screen bg-zonex-white text-zonex-gray-900">
            {/* Hero Section */}
            <section className="py-28 bg-gradient-to-br from-zonex-white to-zonex-lightGray relative overflow-hidden">
                {/* Hero content */}
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-zonex-red/10 rounded-full blur-3xl animate-float-slow"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-zonex-red/15 rounded-full blur-3xl animate-float-slow-reverse"></div>
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center max-w-4xl mx-auto">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-zonex-gray-900">
                            Get in <span className="text-zonex-red">Touch</span>
                        </h1>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-8 rounded-full shadow-red-glow"></div>
                        <p className="text-lg md:text-xl text-zonex-gray-700 mb-8">
                            Have questions, feedback, or partnership opportunities? We'd love to hear from you.
                            Our team is ready to assist with any inquiries about our services.
                        </p>
                    </div>
                </div>

                {/* Animated background */}
                <ContactAnimatedBackground />
            </section>

            {/* Contact Info & Form Section */}
            <section className="py-20 bg-zonex-lightGray relative overflow-hidden">
                {/* Red accent shapes */}
                <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-zonex-red/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-zonex-red/5 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                        {/* Contact Info */}
                        <div>
                            <h2 className="text-3xl font-bold mb-8 text-zonex-gray-900">Contact Information</h2>

                            <div className="space-y-8">
                                <div className="flex items-start text-left">
                                    <div className="p-3 bg-zonex-red/10 rounded-lg mr-4">
                                        <MapPin className="w-6 h-6 text-zonex-red" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold mb-2 text-zonex-gray-900">Our Location</h3>
                                        <p className="text-zonex-gray-700">
                                            Dodhmane complex, 1st floor, Muddinaplaya main road, Vishwaneedam Post, Bangalore-560091
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start text-left">
                                    <div className="p-3 bg-zonex-red/10 rounded-lg mr-4">
                                        <Phone className="w-6 h-6 text-zonex-red" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold mb-2 text-zonex-gray-900">Phone Number</h3>
                                        <p className="text-zonex-gray-700">+91 84315 13412</p>
                                    </div>
                                </div>

                                <div className="flex items-start text-left">
                                    <div className="p-3 bg-zonex-red/10 rounded-lg mr-4">
                                        <Mail className="w-6 h-6 text-zonex-red" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold mb-2 text-zonex-gray-900">Email Address</h3>
                                        <p className="text-zonex-gray-700">info@zone-x.in</p>
                                    </div>
                                </div>

                                <div className="flex items-start text-left">
                                    <div className="p-3 bg-zonex-red/10 rounded-lg mr-4">
                                        <Clock className="w-6 h-6 text-zonex-red" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold mb-2 text-zonex-gray-900">Working Hours</h3>
                                        <p className="text-zonex-gray-700">
                                            Monday - Friday: 9:00 AM - 6:00 PM<br />
                                            Saturday: 10:00 AM - 4:00 PM<br />
                                            Sunday: Closed
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Social Media Links */}
                            <div className="mt-12">
                                <h3 className="text-xl font-semibold mb-4 text-zonex-gray-900">Connect With Us</h3>
                                <div className="flex space-x-4">
                                    <a
                                        href="#"
                                        className="p-3 bg-zonex-red/10 rounded-full hover:bg-zonex-red hover:text-white transition-all duration-300"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"></path>
                                        </svg>
                                    </a>
                                    <a
                                        href="#"
                                        className="p-3 bg-zonex-red/10 rounded-full hover:bg-zonex-red hover:text-white transition-all duration-300"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"></path>
                                        </svg>
                                    </a>
                                    <a
                                        href="#"
                                        className="p-3 bg-zonex-red/10 rounded-full hover:bg-zonex-red hover:text-white transition-all duration-300"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0z"></path>
                                            <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8z"></path>
                                        </svg>
                                    </a>
                                    <a
                                        href="#"
                                        className="p-3 bg-zonex-red/10 rounded-full hover:bg-zonex-red hover:text-white transition-all duration-300"
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M18.335 18.339H15.67v-4.177c0-0.996-0.02-2.278-1.39-2.278-1.389 0-1.601 1.084-1.601 2.205v4.25h-2.666V9.75h2.56v1.17h0.035c0.358-0.674 1.228-1.387 2.528-1.387 2.7 0 3.2 1.778 3.2 4.091v4.715zM7.003 8.575a1.546 1.546 0 01-1.548-1.549 1.548 1.548 0 111.547 1.549zm1.336 9.764H5.666V9.75H8.34v8.589zM19.67 3H4.329C3.593 3 3 3.58 3 4.297v15.406C3 20.42 3.594 21 4.328 21h15.338C20.4 21 21 20.42 21 19.703V4.297C21 3.58 20.4 3 19.666 3h0.003z"></path>
                                        </svg>
                                    </a>
                                </div>
                            </div>

                            {/* Map */}
                            <div className="mt-12">
                                <h3 className="text-xl font-semibold mb-4 text-zonex-gray-900">Find Us On The Map</h3>
                                <div className="rounded-xl overflow-hidden h-64 bg-zonex-white border border-zonex-red/30 shadow-red-lg relative">
                                   <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1374.6343541160084!2d77.48744232050723!3d12.968454434888883!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae3d4d213d84e3%3A0xdd44f0499ff73a4a!2sZone!5e0!3m2!1sen!2sin!4v1746710193710!5m2!1sen!2sin" 
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        allowFullScreen=""
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                    ></iframe>
                                </div>
                            </div>
                        </div>

                        {/* Contact Form - Direct Firebase submission */}
                        <div className="bg-zonex-white rounded-xl p-8 border border-zonex-red/30 shadow-md relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-zonex-red/10 rounded-full blur-3xl"></div>
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-zonex-red/10 rounded-full blur-3xl"></div>

                            <h2 className="text-3xl font-bold mb-8 relative text-zonex-gray-900">Send Us A Message</h2>

                            {formSubmitted ? (
                                <div className="text-center py-12">
                                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-zonex-red/20 mb-4">
                                        <svg className="w-8 h-8 text-zonex-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-2 text-zonex-gray-900">Message Sent!</h3>
                                    <p className="text-zonex-gray-700 mb-6">
                                        Thank you for reaching out. We'll get back to you shortly.
                                    </p>
                                    <button
                                        onClick={() => setFormSubmitted(false)}
                                        className="bg-zonex-red text-white px-6 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300"
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="relative">
                                    {formError && (
                                        <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded-lg mb-6">
                                            {errorMessage || 'Please fill in all required fields.'}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className="block text-zonex-gray-700 mb-2" htmlFor="name">
                                                Your Name *
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 rounded-lg bg-zonex-white border border-gray-300 text-zonex-gray-900 focus:outline-none focus:ring-2 focus:ring-zonex-red/50 focus:border-transparent transition-all"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-zonex-gray-700 mb-2" htmlFor="email">
                                                Your Email *
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 rounded-lg bg-zonex-white border border-gray-300 text-zonex-gray-900 focus:outline-none focus:ring-2 focus:ring-zonex-red/50 focus:border-transparent transition-all"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                        <div>
                                            <label className="block text-zonex-gray-700 mb-2" htmlFor="phone">
                                                Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 rounded-lg bg-zonex-white border border-gray-300 text-zonex-gray-900 focus:outline-none focus:ring-2 focus:ring-zonex-red/50 focus:border-transparent transition-all"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-zonex-gray-700 mb-2" htmlFor="subject">
                                                Subject
                                            </label>
                                            <input
                                                type="text"
                                                id="subject"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 rounded-lg bg-zonex-white border border-gray-300 text-zonex-gray-900 focus:outline-none focus:ring-2 focus:ring-zonex-red/50 focus:border-transparent transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        <label className="block text-zonex-gray-700 mb-2" htmlFor="service">
                                            Service of Interest
                                        </label>
                                        <select
                                            id="service"
                                            name="service"
                                            value={formData.service}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 rounded-lg bg-zonex-white border border-gray-300 text-zonex-gray-900 focus:outline-none focus:ring-2 focus:ring-zonex-red/50 focus:border-transparent transition-all"
                                        >
                                            <option value="general">General Inquiry</option>
                                            <option value="parking">Parking Solutions</option>
                                            <option value="mechanics">Mechanic Services</option>
                                            <option value="spares">Spare Parts</option>
                                            <option value="partnership">Business Partnership</option>
                                            <option value="support">Technical Support</option>
                                        </select>
                                    </div>

                                    <div className="mb-6">
                                        <label className="block text-zonex-gray-700 mb-2" htmlFor="message">
                                            Your Message *
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleInputChange}
                                            rows="5"
                                            className="w-full px-4 py-3 rounded-lg bg-zonex-white border border-gray-300 text-zonex-gray-900 focus:outline-none focus:ring-2 focus:ring-zonex-red/50 focus:border-transparent transition-all"
                                            required
                                        ></textarea>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`bg-zonex-red text-white px-8 py-3 rounded-full hover:bg-zonex-darkRed transition-all duration-300 flex items-center shadow-md ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                                    >
                                        {isSubmitting ? (
                                            <>Processing <span className="ml-2 animate-pulse">...</span></>
                                        ) : (
                                            <>Send Message <Send className="w-4 h-4 ml-2" /></>
                                        )}
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-zonex-white relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1/2 h-1/2 bg-zonex-red/15 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-1/3 h-1/3 bg-zonex-red/25 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-zonex-gray-900">Frequently Asked Questions</h2>
                        <div className="w-20 h-1 bg-zonex-red mx-auto mb-6 rounded-full shadow-red-glow"></div>
                        <p className="text-zonex-gray-700 max-w-2xl mx-auto">
                            Find quick answers to common questions about ZoneX services
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto">
                        <FAQAccordion
                            faqs={[
                                {
                                    question: "How do I book a parking spot using ZoneX?",
                                    answer: "Booking a parking spot with ZoneX is simple. Download our app, create an account, search for parking locations near your destination, select your preferred spot, choose your parking duration, and complete the payment. You'll receive a confirmation with a QR code to use when you arrive at the parking location."
                                },
                                {
                                    question: "Can I book a mechanic for emergency repairs?",
                                    answer: "Yes, ZoneX offers emergency mechanic services. In the app, select the 'Emergency Service' option under the Mechanics section, describe your issue, and our system will match you with the nearest available mechanic. You can track their arrival in real-time through the app."
                                },
                                {
                                    question: "How do I know if spare parts are compatible with my vehicle?",
                                    answer: "Our spare parts platform allows you to input your vehicle make, model, and year to ensure compatibility. Each product listing includes compatibility information, and you can also chat with our parts specialists if you have specific questions about fitment."
                                },
                                {
                                    question: "What payment methods are accepted on ZoneX?",
                                    answer: "ZoneX accepts various payment methods including credit/debit cards, UPI, net banking, and popular mobile wallets. All transactions are secured with industry-standard encryption to ensure your financial information remains safe."
                                },
                                {
                                    question: "How can I become a ZoneX parking or service partner?",
                                    answer: "We're always looking to expand our network of partners. If you own a parking facility or run a mechanic service, visit our 'Partner With Us' section in the app or website. Fill out the application form, and our business development team will contact you within 48 hours to discuss the partnership details."
                                },
                                {
                                    question: "Is the ZoneX app available on both iOS and Android?",
                                    answer: "Yes, the ZoneX app is available for download on both iOS and Android platforms. You can find it in the Apple App Store or Google Play Store by searching for 'ZoneX Mobility'."
                                }
                            ]}
                        />
                    </div>
                </div>
            </section>

            {/* Animation Styles */}
            <style jsx>{`
        @keyframes float-slow {
          0% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
          100% { transform: translateY(0); }
        }
        
        @keyframes float-slow-reverse {
          0% { transform: translateY(0); }
          50% { transform: translateY(20px); }
          100% { transform: translateY(0); }
        }
        
        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }
        
        .animate-float-slow-reverse {
          animation: float-slow-reverse 12s ease-in-out infinite;
        }
        
        .shadow-red-g辅 {
          box-shadow: 0 0 15px rgba(252, 63, 63, 0.4);
        }
        
        .shadow-md {
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }
        
        .shadow-red-lg {
          box-shadow: 0 10px 15px -3px rgba(252, 63, 63, 0.1), 0 4px 6px -2px rgba(252, 63, 63, 0.05);
        }
      `}</style>
        </div>
    );
};

// Animated background for contact page
const ContactAnimatedBackground = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(
            75,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );

        const renderer = new THREE.WebGLRenderer({
            canvas: canvasRef.current,
            alpha: true
        });

        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setClearColor(0x000000, 0);

        // Create particles
        const particlesGeometry = new THREE.BufferGeometry();
        const particlesCnt = 500;
        const posArray = new Float32Array(particlesCnt * 3);

        for (let i = 0; i < particlesCnt * 3; i++) {
            posArray[i] = (Math.random() - 0.5) * 10;
        }

        particlesGeometry.setAttribute('position', new THREE.BufferAttribute(posArray, 3));

        // Create particle material with custom shader for red glow
        const particlesMaterial = new THREE.PointsMaterial({
            size: 0.02,
            color: 0xFC3F3F,
            transparent: true,
            opacity: 0.8,
            blending: THREE.AdditiveBlending
        });

        // Create mesh
        const particlesMesh = new THREE.Points(particlesGeometry, particlesMaterial);
        scene.add(particlesMesh);

        // Position camera
        camera.position.z = 5;

        // Lines connecting particles
        const lineMaterial = new THREE.LineBasicMaterial({
            color: 0xFC3F3F,
            transparent: true,
            opacity: 0.2
        });

        const lines = [];
        const maxDistance = 1; // Max distance for lines to connect

        // Animation
        let mouseX = 0;
        let mouseY = 0;

        document.addEventListener('mousemove', (event) => {
            mouseX = (event.clientX / window.innerWidth) * 2 - 1;
            mouseY = -(event.clientY / window.innerHeight) * 2 + 1;
        });

        function connectParticles() {
            // Remove old lines
            lines.forEach(line => {
                scene.remove(line);
            });
            lines.length = 0;

            // Get particle positions
            const positions = particlesMesh.geometry.attributes.position.array;

            // Check distances between particles and create lines
            for (let i = 0; i < positions.length; i += 3) {
                for (let j = i + 3; j < positions.length; j += 3) {
                    const x1 = positions[i];
                    const y1 = positions[i + 1];
                    const z1 = positions[i + 2];

                    const x2 = positions[j];
                    const y2 = positions[j + 1];
                    const z2 = positions[j + 2];

                    const distance = Math.sqrt(
                        Math.pow(x2 - x1, 2) +
                        Math.pow(y2 - y1, 2) +
                        Math.pow(z2 - z1, 2)
                    );

                    if (distance < maxDistance && Math.random() > 0.99) {
                        const lineGeometry = new THREE.BufferGeometry().setFromPoints([
                            new THREE.Vector3(x1, y1, z1),
                            new THREE.Vector3(x2, y2, z2)
                        ]);

                        const line = new THREE.Line(lineGeometry, lineMaterial);
                        scene.add(line);
                        lines.push(line);

                        // Limit number of lines for performance
                        if (lines.length > 100) break;
                    }
                }

                // Limit number of lines for performance
                if (lines.length > 100) break;
            }
        }

        let frame = 0;
        const animate = () => {
            requestAnimationFrame(animate);
            frame++;

            // Rotate particles
            particlesMesh.rotation.x += 0.0003;
            particlesMesh.rotation.y += 0.0002;

            // React to mouse movement
            particlesMesh.rotation.x += mouseY * 0.0001;
            particlesMesh.rotation.y += mouseX * 0.0001;

            // Only reconnect particles every 30 frames for performance
            if (frame % 30 === 0) {
                connectParticles();
            }

            renderer.render(scene, camera);
        };

        // Handle resize
        window.addEventListener('resize', () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        });

        animate();

        return () => {
            // Cleanup
            particlesGeometry.dispose();
            particlesMaterial.dispose();
            lines.forEach(line => {
                line.geometry.dispose();
                scene.remove(line);
            });
            scene.remove(particlesMesh);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 z-0"
        />
    );
};

// FAQ accordion component
const FAQAccordion = ({ faqs }) => {
    const [openIndex, setOpenIndex] = useState(null);

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className="space-y-4">
            {faqs.map((faq, index) => (
                <div
                    key={index}
                    className={`bg-gradient-to-r ${openIndex === index
                        ? 'from-zonex-red/10 to-zonex-white'
                        : 'from-zonex-white to-zonex-lightGray'
                        } rounded-xl overflow-hidden transition-all duration-300 shadow-md`}
                >
                    <button
                        className="w-full px-6 py-4 text-left flex justify-between items-center"
                        onClick={() => toggleAccordion(index)}
                    >
                        <span className="text-lg font-semibold text-zonex-gray-900">{faq.question}</span>
                        <span className={`transform transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                        </span>
                    </button>
                    <div
                        className={`px-6 overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-72 py-4' : 'max-h-0 py-0'
                            }`}
                    >
                        <p className="text-zonex-gray-700">{faq.answer}</p>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ContactPage;